import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Type,
  Palette,
  Clock,
  Sparkles,
  ChevronDown,
  Check,
} from "lucide-react";

// Phase 10C — Editor-side title card management panel.
// Props:
//   project              — current project doc with `title_cards` + `clips`
//   onPatchTitleCards(cards) — persists the full list via PATCH /projects/:id

const CARD_TYPES = [
  { value: "intro", label: "Intro", desc: "Vor allen Clips" },
  { value: "chapter", label: "Kapitel", desc: "Zwischen Clips" },
  { value: "outro", label: "Outro", desc: "Am Ende" },
];

const ANIM_OPTIONS = [
  { value: "fade", label: "Sanft einblenden" },
  { value: "slide", label: "Hineinwischen" },
  { value: "zoom", label: "Heranzoomen" },
];

const BG_PRESETS = [
  { value: "#000000", label: "Schwarz" },
  { value: "#ffffff", label: "Weiß" },
  { value: "#0b1d3a", label: "Dunkelblau" },
  { value: "#1a1a1a", label: "Anthrazit" },
];

function newCard(type) {
  return {
    id: crypto?.randomUUID?.() || `card-${Date.now()}-${Math.random()}`,
    type,
    position: type === "intro" ? "before" : type === "outro" ? "after" : "between",
    after_clip_index: type === "chapter" ? 0 : null,
    duration_sec: 3.0,
    title: "",
    subtitle: "",
    bg_color: "#000000",
    text_color: "#ffffff",
    animation: "fade",
    font_size: "medium",
  };
}

export default function TitleCardsPanel({ project, onPatchTitleCards }) {
  const cards = project.title_cards || [];
  const clipsCount = (project.clips || []).length;
  const [open, setOpen] = useState(null); // card id currently expanded

  const addCard = (type) => {
    const next = [...cards, newCard(type)];
    onPatchTitleCards(next);
  };
  const updateCard = (id, patch) => {
    const next = cards.map((c) => (c.id === id ? { ...c, ...patch } : c));
    onPatchTitleCards(next);
  };
  const removeCard = (id) => {
    const next = cards.filter((c) => c.id !== id);
    onPatchTitleCards(next);
  };

  return (
    <section
      className="rounded-2xl border border-[#2a2a30] bg-[#0d0d0e]/70 p-4 sm:p-6 space-y-4"
      data-testid="editor-title-cards-panel"
    >
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#1f1f23] text-amber-300">
            <Type size={16} />
          </span>
          <div>
            <h3 className="text-base font-semibold text-white">Titelkarten</h3>
            <p className="text-xs text-[#9a9aa3]">
              Anfang, Kapitel-Trenner und Ende mit eigenem Titel.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {CARD_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => addCard(t.value)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium text-white bg-[#1f1f23] hover:bg-[#2a2a30] transition-colors"
              data-testid={`editor-title-add-${t.value}`}
              title={t.desc}
            >
              <Plus size={13} />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {cards.length === 0 ? (
        <p className="text-xs text-[#6a6a73] py-4 text-center">
          Noch keine Titelkarten. Klick oben auf <strong>Intro</strong>,{" "}
          <strong>Kapitel</strong> oder <strong>Outro</strong>.
        </p>
      ) : (
        <ul className="space-y-2">
          {cards.map((c) => (
            <li
              key={c.id}
              className="rounded-lg border border-[#2a2a30] bg-[#0a0a0b]"
              data-testid={`editor-title-card-${c.id}`}
            >
              <div className="flex items-center gap-3 px-3 py-2.5">
                <div
                  className="w-12 h-12 rounded-md border border-[#2a2a30] shrink-0 flex items-center justify-center"
                  style={{ background: c.bg_color }}
                >
                  <span
                    className="text-[10px] font-bold truncate px-1"
                    style={{ color: c.text_color }}
                    title={c.title}
                  >
                    {c.title || "—"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {c.title || "(ohne Titel)"}
                  </div>
                  <div className="text-[11px] text-[#9a9aa3]">
                    {CARD_TYPES.find((t) => t.value === c.type)?.label} ·{" "}
                    {c.duration_sec.toFixed(1)} s · {ANIM_OPTIONS.find((a) => a.value === c.animation)?.label}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(open === c.id ? null : c.id)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23]"
                  data-testid={`editor-title-edit-${c.id}`}
                  aria-label="Bearbeiten"
                >
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${open === c.id ? "rotate-180" : ""}`}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => removeCard(c.id)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-[#9a9aa3] hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`editor-title-remove-${c.id}`}
                  aria-label="Löschen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {open === c.id && (
                <CardEditor
                  card={c}
                  clipsCount={clipsCount}
                  onChange={(patch) => updateCard(c.id, patch)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CardEditor({ card, clipsCount, onChange }) {
  return (
    <div className="border-t border-[#2a2a30] px-4 py-4 space-y-3 bg-[#0d0d0e]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="block mb-1 text-xs text-[#9a9aa3] uppercase tracking-wider font-medium">
            Titel
          </span>
          <input
            type="text"
            maxLength={80}
            value={card.title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="w-full h-9 px-2.5 rounded-md bg-[#0a0a0b] border border-[#2a2a30] text-white focus:outline-none focus:border-amber-400"
            data-testid={`editor-title-input-${card.id}`}
          />
        </label>
        <label className="block text-sm">
          <span className="block mb-1 text-xs text-[#9a9aa3] uppercase tracking-wider font-medium">
            Untertitel (optional)
          </span>
          <input
            type="text"
            maxLength={120}
            value={card.subtitle || ""}
            onChange={(e) => onChange({ subtitle: e.target.value })}
            className="w-full h-9 px-2.5 rounded-md bg-[#0a0a0b] border border-[#2a2a30] text-white focus:outline-none focus:border-amber-400"
            data-testid={`editor-subtitle-input-${card.id}`}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="flex items-center justify-between text-xs uppercase tracking-wider text-[#9a9aa3] font-medium mb-1.5">
            <span>Dauer</span>
            <span className="text-white normal-case tracking-normal tabular-nums">
              {card.duration_sec.toFixed(1)} s
            </span>
          </label>
          <input
            type="range"
            min={1}
            max={10}
            step={0.5}
            value={card.duration_sec}
            onChange={(e) => onChange({ duration_sec: parseFloat(e.target.value) })}
            className="w-full accent-amber-400"
            data-testid={`editor-duration-${card.id}`}
          />
        </div>
        {card.position === "between" && (
          <label className="block text-sm">
            <span className="block mb-1 text-xs text-[#9a9aa3] uppercase tracking-wider font-medium">
              Position
            </span>
            <select
              value={card.after_clip_index ?? 0}
              onChange={(e) => onChange({ after_clip_index: parseInt(e.target.value, 10) })}
              className="w-full h-9 px-2 rounded-md bg-[#0a0a0b] border border-[#2a2a30] text-white text-sm"
              data-testid={`editor-position-${card.id}`}
            >
              {Array.from({ length: Math.max(0, clipsCount - 1) }, (_, i) => (
                <option key={i} value={i}>
                  Nach Clip {i + 1}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <span className="block mb-1.5 text-xs text-[#9a9aa3] uppercase tracking-wider font-medium">
            Hintergrund
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {BG_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => onChange({ bg_color: p.value })}
                className={`w-8 h-8 rounded-full border-2 transition-colors ${
                  card.bg_color.toLowerCase() === p.value
                    ? "border-amber-400"
                    : "border-[#2a2a30] hover:border-[#3a3a40]"
                }`}
                style={{ background: p.value }}
                title={p.label}
                aria-label={p.label}
                data-testid={`editor-bg-${p.value.slice(1)}-${card.id}`}
              />
            ))}
            <input
              type="color"
              value={card.bg_color}
              onChange={(e) => onChange({ bg_color: e.target.value })}
              className="w-8 h-8 rounded-full border-2 border-[#2a2a30] cursor-pointer"
              title="Eigene Farbe"
            />
          </div>
        </div>
        <div>
          <span className="block mb-1.5 text-xs text-[#9a9aa3] uppercase tracking-wider font-medium">
            Animation
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {ANIM_OPTIONS.map((a) => (
              <button
                key={a.value}
                type="button"
                onClick={() => onChange({ animation: a.value })}
                className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium transition-colors ${
                  card.animation === a.value
                    ? "bg-amber-500/15 text-amber-100 border border-amber-500/50"
                    : "bg-[#1f1f23] text-[#9a9aa3] hover:text-white"
                }`}
                data-testid={`editor-animation-${a.value}-${card.id}`}
              >
                {card.animation === a.value && <Check size={11} />}
                {a.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="block mb-1.5 text-xs text-[#9a9aa3] uppercase tracking-wider font-medium">
            Schriftgröße
          </span>
          <select
            value={card.font_size || "medium"}
            onChange={(e) => onChange({ font_size: e.target.value })}
            className="h-9 px-3 rounded-md bg-[#1f1f23] border border-[#2a2a30] focus:border-amber-500 outline-none text-white text-sm"
            data-testid={`editor-fontsize-${card.id}`}
          >
            <option value="small">Klein</option>
            <option value="medium">Mittel</option>
            <option value="large">Groß</option>
          </select>
        </div>
      </div>
    </div>
  );
}
